import { useRef, useState } from "react";
import { Paperclip, X, FileText, FileSpreadsheet, Image as ImageIcon, Loader2 } from "lucide-react";

export interface UploadedFile {
  name: string;
  mimeType: string;
  size: number;
  data: string;
}

interface Props {
  files: UploadedFile[];
  onChange: (files: UploadedFile[]) => void;
  maxFiles?: number;
  maxFileMB?: number;
  className?: string;
  label?: string;
}

const ACCEPT = ".jpg,.jpeg,.png,.gif,.webp,.heic,.heif,.pdf,.xls,.xlsx,.csv,image/*,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv";

const ALLOWED_MIME = new Set([
  "image/jpeg", "image/png", "image/gif", "image/webp", "image/heic", "image/heif",
  "application/pdf",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/csv",
]);

const ALLOWED_EXT = /\.(jpe?g|png|gif|webp|heic|heif|pdf|xlsx?|csv)$/i;

function fileIcon(mimeType: string) {
  if (mimeType.startsWith("image/")) return ImageIcon;
  if (mimeType.includes("sheet") || mimeType === "application/vnd.ms-excel" || mimeType === "text/csv") return FileSpreadsheet;
  return FileText;
}

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const result = reader.result as string;
      const idx = result.indexOf(",");
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

export default function FileUploader({
  files, onChange, maxFiles = 8, maxFileMB = 10, className = "", label = "Attach files (photos, PDF, Excel)",
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const handleFiles = async (list: FileList | null) => {
    if (!list || list.length === 0) return;
    setError(null);
    setBusy(true);
    try {
      const max = maxFileMB * 1024 * 1024;
      const incoming: UploadedFile[] = [];
      for (const f of Array.from(list)) {
        if (files.length + incoming.length >= maxFiles) {
          setError(`You can attach up to ${maxFiles} files.`);
          break;
        }
        const isAllowedType = ALLOWED_MIME.has(f.type) || ALLOWED_EXT.test(f.name);
        if (!isAllowedType) {
          setError(`"${f.name}" isn't allowed. Use images, PDF or Excel.`);
          continue;
        }
        if (f.size > max) {
          setError(`"${f.name}" is over ${maxFileMB} MB.`);
          continue;
        }
        const data = await readAsBase64(f);
        const mimeType = ALLOWED_MIME.has(f.type)
          ? f.type
          : f.name.toLowerCase().endsWith(".pdf") ? "application/pdf"
          : f.name.toLowerCase().endsWith(".xlsx") ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : f.name.toLowerCase().endsWith(".xls") ? "application/vnd.ms-excel"
          : f.name.toLowerCase().endsWith(".csv") ? "text/csv"
          : f.type || "application/octet-stream";
        incoming.push({ name: f.name, mimeType, size: f.size, data });
      }
      if (incoming.length > 0) onChange([...files, ...incoming]);
    } catch (err) {
      setError("Failed to read one of the files. Please try again.");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (idx: number) => onChange(files.filter((_, i) => i !== idx));

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-foreground mb-2">{label}</label>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); }}
        onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
        className="border-2 border-dashed border-slate-300 hover:border-primary/50 hover:bg-primary/5 rounded-lg px-4 py-5 cursor-pointer transition-colors text-center"
      >
        <Paperclip className="w-5 h-5 text-muted-foreground mx-auto mb-1.5" />
        <p className="text-sm text-foreground font-medium">Click to upload or drag & drop</p>
        <p className="text-xs text-muted-foreground mt-0.5">Images, PDF or Excel · up to {maxFileMB} MB · {maxFiles} files max</p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>
      {busy && (
        <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Reading files…
        </div>
      )}
      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
      {files.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {files.map((f, i) => {
            const Icon = fileIcon(f.mimeType);
            return (
              <li key={i} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5 text-sm">
                <Icon className="w-4 h-4 text-primary shrink-0" />
                <span className="flex-1 min-w-0 truncate text-foreground">{f.name}</span>
                <span className="text-xs text-muted-foreground shrink-0">{(f.size / 1024).toFixed(0)} KB</span>
                <button type="button" onClick={() => remove(i)} className="p-1 rounded hover:bg-red-50 text-muted-foreground hover:text-red-600 transition-colors" aria-label="Remove">
                  <X className="w-3.5 h-3.5" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
